import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import Box from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/Box';
import MetaFormInline from 'Components/UI/Meta/MetaForm/Components/MetaFormInline';
import StepNavigator from 'Components/ComponentLibrary/Molecules/StepNavigator';
import fr from 'facet-rest';
import WizardSpecificPageLinkBottom
  from 'Components/ComponentLibrary/Components/Uncategorized/WizardSpecificPageLinkBottom';
import {
  useWizardStepWrapperTemplate,
} from 'Views/Page/Content/Templates/WizardTemplates/WizardStepWrapperTemplate/useWizardStepWrapperTemplate';
import { FDConfirmationDialog } from '@facet-wealth/design';

const COLORS = [
  'primary',
  'primaryBlueMuted',
  'white',
];

const WizardStepWrapperTemplate = ({
  className,
  formClassName,
  headerTextColor,
  primaryActionLabel,
  template,
  resource,
  layout,
  metaOverrideSet,
  onNext,
  onClose,
  onChange,
  LayoutProps,
  showMobileSideNavigationMenu,
  headerTransparentBackground,
  hideBack,
  hideErrors,
  SpecificPageLinkComponent = WizardSpecificPageLinkBottom,
  onSpecificLinkClicked,
  submitConfirmation,
  disableNextButton,
  submitConfirmationTitle,
  submitConfirmationMessage,
  submitConfirmationButtonLabel,
}) => {
  const {
    handleOnNext,
    handlePageBottomLinkClick,
    onPrevious,
    onErrorLinkClick,
    dialogOpen,
    handleSubmit,
    handleClose,
    errors,
    loading,
    isPageContentLoading,
    classes,
    header,
  } = useWizardStepWrapperTemplate({
    resource,
    template,
    onNext,
    onClose,
    hideBack,
    onSpecificLinkClicked,
    disableNextButton,
    submitConfirmation,
    headerTextColor,
    showMobileSideNavigationMenu,
    headerTransparentBackground,
  });
  return (
    <Box className={classNames(className, classes.root)}>
      {submitConfirmation
        ? <FDConfirmationDialog
          open={dialogOpen}
          title={submitConfirmationTitle}
          message={submitConfirmationMessage}
          confirmLabel={submitConfirmationButtonLabel}
          loading={loading}
          onConfirm={async () => {
            await handleSubmit();
          }}
          onClose={() => {
            handleClose();
          }}
        /> : null }
      {header}
      <MetaFormInline
        className={classNames(classes.form, formClassName)}
        noIndividualSubmitButtons
        resource={resource}
        LayoutProps={{
          errorListClassName: classes.errorList,
          errors,
          ...LayoutProps,
        }}
        layout={layout}
        margin={'none'}
        padding={'none'}
        metaOverrideSet={metaOverrideSet}
        errorsOverride={!hideErrors && errors}
        onErrorLinkClick={onErrorLinkClick}
        onChange={onChange}
      />
      <div className={classes.navigation}>
        <SpecificPageLinkComponent
          resource={resource}
          onClick={handlePageBottomLinkClick}
        />
        <StepNavigator
          onNext={handleOnNext()}
          onPrevious={onPrevious}
          isLoading={isPageContentLoading || loading}
        >
          {resource.attr('primaryActionLabel') || primaryActionLabel}
        </StepNavigator>
      </div>
    </Box>
  );
};

WizardStepWrapperTemplate.defaultProps = {
  className: '',
  formClassName: '',
  headerTextColor: 'primaryBlueMuted',
  primaryActionLabel: 'Continue',
  metaOverrideSet: undefined,
  template: '',
  onNext: undefined,
  onClose: undefined,
  onChange: undefined,
  LayoutProps: {},
  showMobileSideNavigationMenu: false,
  headerTransparentBackground: false,
  hideBack: false,
  hideErrors: false,
  SpecificPageLinkComponent: WizardSpecificPageLinkBottom,
  onSpecificLinkClicked: undefined,
  submitConfirmation: false,
  submitConfirmationMessage: null,
  submitConfirmationTitle: 'Are you sure?',
  submitConfirmationButtonLabel: 'Continue',
  disableNextButton: false,
};

WizardStepWrapperTemplate.propTypes = {
  className: PropTypes.string,
  formClassName: PropTypes.string,
  headerTextColor: PropTypes.oneOf(COLORS),
  primaryActionLabel: PropTypes.string,
  /** Template is used for Amplitude logging */
  template: PropTypes.string,
  /** layout: The body content layout * */
  layout: PropTypes.oneOfType([PropTypes.element, PropTypes.func, PropTypes.node]).isRequired,
  /** metaOverrideSet: A meta override set * */
  metaOverrideSet: PropTypes.object,
  /** resource: The displayed resource * */
  resource: PropTypes.instanceOf(fr.Resource).isRequired,
  onNext: PropTypes.func,
  onClose: PropTypes.func,
  onChange: PropTypes.func,
  LayoutProps: PropTypes.object,
  showMobileSideNavigationMenu: PropTypes.bool,
  headerTransparentBackground: PropTypes.bool,
  hideBack: PropTypes.bool,
  hideErrors: PropTypes.bool,
  SpecificPageLinkComponent: PropTypes.func,
  onSpecificLinkClicked: PropTypes.func,
  submitConfirmation: PropTypes.bool,
  submitConfirmationTitle: PropTypes.string,
  submitConfirmationMessage: PropTypes.string,
  submitConfirmationButtonLabel: PropTypes.string,
  disableNextButton: PropTypes.bool,
};

export default WizardStepWrapperTemplate;
